import React, { useEffect, useState } from 'react';
import { editorUrl } from '@/lib/editorBasePath';
import { Loader2, History, RotateCcw } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { YearbookElement, PageBackground, PAGE_WIDTH, PAGE_HEIGHT } from '@/types/yearbook';
import { normalizeElements } from '@/lib/normalizeElements';
import ReadOnlyElement from './ReadOnlyElement';

interface PageHistoryDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  pageId: string;
  pageLabel: string;
  onRestore: (elements: YearbookElement[], background?: PageBackground) => void;
}

interface HistoryEntry {
  id: string | number;
  created_at?: string | null;
  user_name?: string | null;
  elements: YearbookElement[];
  background?: PageBackground;
}

const THUMB_SCALE = 0.28;
const THUMB_W = PAGE_WIDTH * THUMB_SCALE;
const THUMB_H = PAGE_HEIGHT * THUMB_SCALE;

const PageHistoryDialog: React.FC<PageHistoryDialogProps> = ({ open, onOpenChange, pageId, pageLabel, onRestore }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [entries, setEntries] = useState<HistoryEntry[]>([]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setEntries([]);

    fetch(editorUrl(`/load_histories?page_id=${encodeURIComponent(pageId)}`), {
      method: 'GET',
      headers: { 'Accept': 'application/json' },
    })
      .then(async (res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then((data) => {
        if (cancelled) return;
        // Accept either an array directly, or { histories: [...] } / { pages: [...] }
        const list: any[] = Array.isArray(data)
          ? data
          : Array.isArray(data?.histories)
          ? data.histories
          : Array.isArray(data?.pages)
          ? data.pages
          : [];

        const parsed: HistoryEntry[] = list.map((item, i) => {
          const op = item.objects_positions ?? item.objectsPositions ?? {};
          return {
            id: item.id ?? item.history_id ?? i,
            created_at: item.created_at ?? item.createdAt ?? null,
            user_name: item.user_name ?? item.userName ?? null,
            elements: normalizeElements(op.elements ?? []),
            background: op.background ?? undefined,
          };
        });
        setEntries(parsed);
      })
      .catch((e) => {
        if (cancelled) return;
        console.error('Failed to load page history:', e);
        setError("Impossible de charger l'historique");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, pageId]);

  const handleRestore = (entry: HistoryEntry) => {
    onRestore(entry.elements, entry.background);
    onOpenChange(false);
  };

  const formatDate = (iso?: string | null) => {
    if (!iso) return '';
    try {
      const d = new Date(iso);
      return d.toLocaleString('fr-FR', {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
      });
    } catch {
      return iso;
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <History size={18} /> Historique de {pageLabel}
          </DialogTitle>
          <DialogDescription>
            Choisissez une version pour restaurer le contenu de la page.
          </DialogDescription>
        </DialogHeader>

        <div className="min-h-[200px] max-h-[60vh] overflow-y-auto">
          {loading && (
            <div className="flex flex-col items-center justify-center py-12 gap-2 text-muted-foreground">
              <Loader2 className="animate-spin" size={24} />
              <span className="text-sm">Chargement de l'historique…</span>
            </div>
          )}

          {!loading && error && (
            <div className="text-center py-12 text-sm text-destructive">{error}</div>
          )}

          {!loading && !error && entries.length === 0 && (
            <div className="text-center py-12 text-sm text-muted-foreground">
              Aucun historique disponible pour cette page.
            </div>
          )}

          {!loading && !error && entries.length > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 py-2">
              {entries.map((entry, idx) => {
                const bgIsImage = entry.background && entry.background.type !== 'color';
                const bgColor = entry.background?.type === 'color' ? entry.background.value : '#ffffff';
                return (
                  <div
                    key={String(entry.id) + idx}
                    className="flex flex-col gap-2 p-2 rounded-lg border border-border bg-card hover:border-primary/40 transition-colors"
                  >
                    <div
                      className="relative overflow-hidden rounded border border-border shadow-sm mx-auto"
                      style={{ width: THUMB_W, height: THUMB_H, backgroundColor: bgColor }}
                    >
                      {bgIsImage && entry.background && (
                        <img
                          src={entry.background.value}
                          alt=""
                          className="absolute inset-0 w-full h-full object-cover pointer-events-none"
                          draggable={false}
                        />
                      )}
                      <div
                        className="origin-top-left"
                        style={{ width: PAGE_WIDTH, height: PAGE_HEIGHT, transform: `scale(${THUMB_SCALE})` }}
                      >
                        {entry.elements.map((el) => (
                          <ReadOnlyElement key={el.id} element={el} />
                        ))}
                      </div>
                    </div>
                    <div className="text-center leading-tight">
                      <div className="text-[11px] text-muted-foreground">
                        {formatDate(entry.created_at) || `Version ${idx + 1}`}
                      </div>
                      {entry.user_name && (
                        <div className="text-[11px] font-medium text-foreground truncate">
                          {entry.user_name}
                        </div>
                      )}
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      className="gap-1.5 text-xs"
                      onClick={() => handleRestore(entry)}
                    >
                      <RotateCcw size={12} />
                      Revenir à cette version
                    </Button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PageHistoryDialog;
